/**
 * Company-scoped branding for Company Management (mirrors server/utils/brandingColors.ts).
 * Kept in client so Vite does not bundle server code.
 */

import type { CSSProperties } from 'react'

import type { CompanyBrandingSettings } from '../core/models/Company'

export interface CompanyBrandingPalette {
  brandColor: string
  brandColorLight: string
  brandColorDark: string
  brandColorShadow: string
  brandColorBg: string
  brandColorText: string
}

function hexToRgb(hex: string): [number, number, number] | null {
  const h = hex.replace('#', '')
  if (h.length === 3) {
    const r = parseInt(h[0] + h[0], 16)
    const g = parseInt(h[1] + h[1], 16)
    const b = parseInt(h[2] + h[2], 16)
    if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return null
    return [r, g, b]
  }
  if (h.length === 6) {
    const r = parseInt(h.slice(0, 2), 16)
    const g = parseInt(h.slice(2, 4), 16)
    const b = parseInt(h.slice(4, 6), 16)
    if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return null
    return [r, g, b]
  }
  return null
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  const rn = r / 255
  const gn = g / 255
  const bn = b / 255
  const max = Math.max(rn, gn, bn)
  const min = Math.min(rn, gn, bn)
  const l = (max + min) / 2
  if (max === min) return [0, 0, l]
  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
  let h: number
  if (max === rn) h = (gn - bn) / d + (gn < bn ? 6 : 0)
  else if (max === gn) h = (bn - rn) / d + 2
  else h = (rn - gn) / d + 4
  return [h * 60, s, l]
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  const hue2rgb = (p: number, q: number, t: number): number => {
    if (t < 0) t += 1
    if (t > 1) t -= 1
    if (t < 1 / 6) return p + (q - p) * 6 * t
    if (t < 1 / 2) return q
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
    return p
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s
  const p = 2 * l - q
  const hn = h / 360
  return [
    Math.round(hue2rgb(p, q, hn + 1 / 3) * 255),
    Math.round(hue2rgb(p, q, hn) * 255),
    Math.round(hue2rgb(p, q, hn - 1 / 3) * 255),
  ]
}

function rgbToHex(r: number, g: number, b: number): string {
  return `#${[r, g, b].map((v) => Math.max(0, Math.min(255, v)).toString(16).padStart(2, '0')).join('')}`
}

function adjustLightness(hex: string, delta: number): string {
  const rgb = hexToRgb(hex)
  if (!rgb) return hex
  const [h, s, l] = rgbToHsl(...rgb)
  const newL = Math.max(0, Math.min(1, l + delta))
  return rgbToHex(...hslToRgb(h, s, newL))
}

function hexToRgba(hex: string, alpha: number): string {
  const rgb = hexToRgb(hex)
  if (!rgb) return `rgba(0,0,0,${alpha})`
  return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})`
}

function channelToLinear(c: number): number {
  const x = c / 255
  return x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4
}

function relativeLuminanceHex(hex: string): number {
  const rgb = hexToRgb(hex)
  if (!rgb) return 0
  const r = channelToLinear(rgb[0])
  const g = channelToLinear(rgb[1])
  const b = channelToLinear(rgb[2])
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

function contrastRatio(fgHex: string, bgHex: string): number {
  const l1 = relativeLuminanceHex(fgHex)
  const l2 = relativeLuminanceHex(bgHex)
  const lighter = Math.max(l1, l2)
  const darker = Math.min(l1, l2)
  return (lighter + 0.05) / (darker + 0.05)
}

function lightTint(hex: string): string {
  const rgb = hexToRgb(hex)
  if (!rgb) return '#fff2d6'
  const [h, s] = rgbToHsl(...rgb)
  return rgbToHex(...hslToRgb(h, Math.min(s, 0.8), 0.95))
}

function readableTextOnLightBackgrounds(primaryHex: string, minContrast = 4.5): string {
  const rgb = hexToRgb(primaryHex)
  if (!rgb) return '#b45309'
  const tintBg = lightTint(primaryHex)
  const white = '#ffffff'
  const [h, sRaw] = rgbToHsl(...rgb)
  const sat = Math.min(sRaw, 0.85)
  let L = 0.32
  for (let i = 0; i < 28; i++) {
    const candidate = rgbToHex(...hslToRgb(h, sat, L))
    if (contrastRatio(candidate, white) >= minContrast && contrastRatio(candidate, tintBg) >= minContrast) {
      return candidate
    }
    L = Math.max(0.05, L - 0.03)
  }
  return '#1a1a1a'
}

const DEFAULT_PRIMARY = '#f97415'

function buildPalette(primary: string): CompanyBrandingPalette {
  return {
    brandColor: primary,
    brandColorLight: adjustLightness(primary, 0.12),
    brandColorDark: adjustLightness(primary, -0.1),
    brandColorShadow: hexToRgba(primary, 0.3),
    brandColorBg: lightTint(primary),
    brandColorText: readableTextOnLightBackgrounds(primary),
  }
}

export const DEFAULT_COMPANY_BRANDING_PALETTE: CompanyBrandingPalette = buildPalette(DEFAULT_PRIMARY)

export function deriveCompanyBrandingPalette(primaryColor?: string | null): CompanyBrandingPalette {
  if (!primaryColor || !hexToRgb(primaryColor)) return DEFAULT_COMPANY_BRANDING_PALETTE
  return buildPalette(primaryColor)
}

/** Analogous hue shift for accent when `settings.accent_color` is unset (Company Management). */
export function deriveDefaultAccentColor(primaryHex: string): string {
  const rgb = hexToRgb(primaryHex)
  if (!rgb) return adjustLightness(primaryHex, 0.08)
  const [h, s, l] = rgbToHsl(...rgb)
  const newH = (h + 22) % 360
  return rgbToHex(...hslToRgb(newH, Math.min(s * 1.05, 0.95), l))
}

function resolveBrandingHex(value: string | undefined | null, fallback: string): string {
  const v = typeof value === 'string' ? value.trim() : ''
  if (v && hexToRgb(v)) return v
  return fallback
}

/** True when `value` is a 3- or 6-digit hex (optional `#`). */
export function isValidCompanyHex(value: string | undefined | null): boolean {
  const v = typeof value === 'string' ? value.trim() : ''
  if (!v) return false
  return hexToRgb(v) !== null
}

function hexRgbTripletForShadow(hex: string): string {
  const rgb = hexToRgb(hex)
  if (!rgb) return '249, 116, 21'
  return `${rgb[0]}, ${rgb[1]}, ${rgb[2]}`
}

/** MUI-friendly contrast on solid `main` (primary button labels). */
function contrastTextOnMain(mainHex: string): string {
  const lum = relativeLuminanceHex(mainHex)
  return lum > 0.179 ? '#1a1a1a' : '#ffffff'
}

export interface CompanyBrandingThemeParts {
  /** Inline / sx `style` for `--sevv-*` overrides inside a scoped wrapper */
  cssVariables: CSSProperties
  /** Deep-merge into MUI theme `palette` */
  muiPalette: {
    primary: { main: string; light: string; dark: string; contrastText: string }
    secondary?: { main: string; light: string; dark: string; contrastText: string }
    info: { main: string; light: string; dark: string; contrastText: string }
  }
  /** Resolved link color for `MuiLink` overrides in [[CompanyBrandedShell]]. */
  linkColorHex: string
}

/**
 * Builds scoped CSS variables and MUI palette overrides from company colors.
 * Optional `brandingSettings` (Parse `Company.settings`) drives accent, link, and surface tint.
 * When primary is missing/invalid, returns null (caller should skip branding shell).
 */
/**
 * A company's brand colour is user-supplied data, and MUI's colour helpers (`alpha`, `lighten`,
 * `darken`) throw on anything they cannot decompose — which takes the whole app down via the
 * error boundary instead of degrading. Anything feeding a *stored* colour into those helpers must
 * come through here.
 *
 * The case that actually shipped: CompanySetup briefly defaulted `primary_color` to the string
 * `var(--sevv-primary, #f97415)` and persisted it, so `alpha(company.primary_color)` in MainMenu
 * threw for every member of any company created through that form. Valid as CSS, fatal as data.
 */
export function brandHexOrFallback(value: string | null | undefined, fallback: string): string {
  return isValidCompanyHex(value) ? (value as string).trim() : fallback
}

export function buildCompanyBrandingThemeParts(
  primaryColor: string | null | undefined,
  secondaryColor?: string | null,
  brandingSettings?: CompanyBrandingSettings | null
): CompanyBrandingThemeParts | null {
  if (!primaryColor || !hexToRgb(primaryColor)) return null

  const p = deriveCompanyBrandingPalette(primaryColor)
  const rgb = hexRgbTripletForShadow(primaryColor)
  const secondaryValid = secondaryColor && hexToRgb(secondaryColor)
  const secMain = secondaryValid ? secondaryColor! : adjustLightness(primaryColor, -0.18)

  const accentHex = resolveBrandingHex(brandingSettings?.accent_color, deriveDefaultAccentColor(primaryColor))
  const linkHex = resolveBrandingHex(brandingSettings?.link_color, p.brandColorDark)
  const surfaceHex = resolveBrandingHex(brandingSettings?.surface_tint_color, p.brandColorBg)

  const cssVariables: CSSProperties = {
    ['--sevv-primary' as string]: p.brandColor,
    ['--sevv-primary-light' as string]: p.brandColorLight,
    ['--sevv-primary-dark' as string]: p.brandColorDark,
    ['--sevv-primary-pale' as string]: surfaceHex,
    ['--sevv-surface-tint' as string]: surfaceHex,
    ['--sevv-accent' as string]: accentHex,
    ['--sevv-info' as string]: accentHex,
    ['--sevv-link' as string]: linkHex,
    ['--sevv-shadow-primary' as string]: `0 4px 14px 0 rgba(${rgb}, 0.3)`,
    ['--sevv-shadow-primary-lg' as string]: `0 10px 25px 0 rgba(${rgb}, 0.4)`,
    ['--sevv-secondary' as string]: secMain,
  }

  const muiPalette: CompanyBrandingThemeParts['muiPalette'] = {
    primary: {
      main: p.brandColor,
      light: p.brandColorLight,
      dark: p.brandColorDark,
      contrastText: contrastTextOnMain(p.brandColor),
    },
    info: {
      main: accentHex,
      light: adjustLightness(accentHex, 0.14),
      dark: adjustLightness(accentHex, -0.12),
      contrastText: contrastTextOnMain(accentHex),
    },
  }

  if (secondaryValid) {
    muiPalette.secondary = {
      main: secondaryColor!,
      light: adjustLightness(secondaryColor!, 0.12),
      dark: adjustLightness(secondaryColor!, -0.1),
      contrastText: contrastTextOnMain(secondaryColor!),
    }
  }

  return { cssVariables, muiPalette, linkColorHex: linkHex }
}
